import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Cloud,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  Loader2,
  Save,
  Sun,
  TrendingUp,
  TrendingDown,
  Calculator,
  PieChart,
  Download,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  ACTIVITY_TYPES,
  fmtEur,
  computeForecast,
  weatherLabel,
} from '../lib/forecastHelpers';
import { MonthGrid, NumInput } from '../components/ForecastInputs';

const WEATHER_ICONS = {
  0: Sun,
  1: CloudSun,
  2: CloudSun,
  3: Cloud,
  45: Cloud,
  48: Cloud,
  51: CloudRain,
  53: CloudRain,
  55: CloudRain,
  56: CloudRain,
  57: CloudRain,
  61: CloudRain,
  63: CloudRain,
  65: CloudRain,
  66: CloudRain,
  67: CloudRain,
  71: CloudSnow,
  73: CloudSnow,
  75: CloudSnow,
  77: CloudSnow,
  80: CloudRain,
  81: CloudRain,
  82: CloudRain,
  85: CloudSnow,
  86: CloudSnow,
  95: CloudLightning,
  96: CloudLightning,
  99: CloudLightning,
};

function getWeatherIcon(code) {
  return WEATHER_ICONS[code] || CloudSun;
}

function getWeatherIconClass(code) {
  if (code === 0) return 'text-amber-500';
  if (code === 1 || code === 2) return 'text-amber-400';
  if (code === 95 || code === 96 || code === 99) return 'text-violet-500';
  if (code === 71 || code === 73 || code === 75 || code === 77 || code === 85 || code === 86) return 'text-sky-300';
  if (code === 51 || code === 53 || code === 55 || code === 61 || code === 63 || code === 65 || code === 80 || code === 81 || code === 82) return 'text-sky-500';
  return 'text-slate-500';
}

const DEFAULT_COMMERCE = { monthly_clients: '', avg_price: '', margin_pct: '', monthly_charges: '', high_season_months: [], low_season_months: [] };
const DEFAULT_SERVICE = { daily_rate: '', active_days_per_month: '', monthly_clients: '', monthly_charges: '', high_season_months: [], low_season_months: [] };
const DEFAULT_RESTAURATION = { daily_covers: '', avg_price: '', fill_rate_pct: '', opening_days_per_month: '', monthly_charges: '' };
const DEFAULT_AUTRE = { monthly_clients: '', avg_price: '', worked_days_per_month: '', monthly_charges: '' };

export const PrevisionnelPage = () => {
  const { api } = useAuth();
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [weatherCity, setWeatherCity] = useState('Marseille');
  const [cityInput, setCityInput] = useState('Marseille');
  const [cityError, setCityError] = useState('');

  const [previousYearData, setPreviousYearData] = useState(null);

  const [profileLoading, setProfileLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [activityType, setActivityType] = useState('');
  const [commerce, setCommerce] = useState(DEFAULT_COMMERCE);
  const [service, setService] = useState(DEFAULT_SERVICE);
  const [restauration, setRestauration] = useState(DEFAULT_RESTAURATION);
  const [autre, setAutre] = useState(DEFAULT_AUTRE);

  const loadWeather = async (city) => {
    setWeatherLoading(true);
    setCityError('');
    try {
      const r = await api.get(`/forecasting/weather?city=${encodeURIComponent(city)}`);
      setOverview(r.data);
      setWeatherCity(r.data.location ? r.data.location.name : city);
    } catch (e) {
      setCityError(e.response?.data?.detail || 'Ville introuvable');
    } finally {
      setWeatherLoading(false);
    }
  };

  useEffect(() => {
    loadWeather('Marseille');
    api.get('/forecasting/previous-year-ca').then((r) => setPreviousYearData(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      setProfileLoading(true);
      try {
        const r = await api.get('/forecasting/business-profile');
        const p = r.data || {};
        if (p.activity_type) setActivityType(p.activity_type);
        if (p.commerce) setCommerce(Object.assign({}, DEFAULT_COMMERCE, p.commerce));
        if (p.service) setService(Object.assign({}, DEFAULT_SERVICE, p.service));
        if (p.restauration) setRestauration(Object.assign({}, DEFAULT_RESTAURATION, p.restauration));
        if (p.autre) setAutre(Object.assign({}, DEFAULT_AUTRE, p.autre));
      } catch (e) {
        // first-time
      } finally {
        setProfileLoading(false);
      }
    };
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentData = useMemo(() => {
    if (activityType === 'commerce') return commerce;
    if (activityType === 'service') return service;
    if (activityType === 'restauration') return restauration;
    if (activityType === 'autre') return autre;
    return null;
  }, [activityType, commerce, service, restauration, autre]);

  const forecast = useMemo(() => computeForecast(activityType, currentData), [activityType, currentData]);

  const handleSave = async () => {
    if (!activityType) {
      toast.error("Veuillez sélectionner un type d'activité");
      return;
    }
    setSavingProfile(true);
    try {
      await api.put('/forecasting/business-profile', {
        activity_type: activityType,
        commerce: commerce,
        service: service,
        restauration: restauration,
        autre: autre,
      });
      toast.success('Prévisionnel enregistré');
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSavingProfile(false);
    }
  };

  const updateCommerce = (k, v) => setCommerce(Object.assign({}, commerce, { [k]: v }));
  const updateService = (k, v) => setService(Object.assign({}, service, { [k]: v }));
  const updateRestauration = (k, v) => setRestauration(Object.assign({}, restauration, { [k]: v }));
  const updateAutre = (k, v) => setAutre(Object.assign({}, autre, { [k]: v }));

  const handleExportPDF = async () => {
    if (!forecast || !activityType) {
      toast.error("Renseignez d'abord votre activité");
      return;
    }
    const labelMap = { commerce: 'Commerce', service: 'Service', restauration: 'Restauration', autre: 'Autre' };
    try {
      const response = await api.post('/forecasting/export/pdf', {
        activity_type: activityType,
        activity_label: labelMap[activityType] || activityType,
        inputs: currentData,
        annual_ca: forecast.annualCA,
        annual_charges: forecast.annualCharges,
        net_profit: forecast.netProfit,
        breakdown: forecast.breakdown,
      }, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `previsionnel_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch (e) {
      toast.error("Erreur lors de l'export PDF");
    }
  };

  const profitColor = forecast && forecast.netProfit >= 0 ? 'text-[#065F46]' : 'text-red-600';
  const profitBoxCls = forecast && forecast.netProfit >= 0
    ? 'rounded-md border p-4 border-emerald-300 bg-emerald-100/60'
    : 'rounded-md border p-4 border-red-300 bg-red-50';
  const breakdownLines = forecast ? forecast.breakdown : [];

  // Weather render values
  const cw = overview ? overview.current_weather : null;
  const cwCode = cw ? cw.weather_code : null;
  const CurrentIcon = getWeatherIcon(cwCode);
  const next7 = overview ? (overview.next_7_days_weather || []) : [];

  return (
    <div className="space-y-6" data-testid="forecast-page">
      <div>
        <h1 className="font-outfit text-2xl md:text-3xl font-bold text-slate-900" data-testid="forecast-page-title">Prévisionnel</h1>
        <p className="text-slate-500 text-sm md:text-base" data-testid="forecast-page-subtitle">
          Météo Marseille + prévisionnel CA / Charges / Bénéfice annuel basé sur votre activité
        </p>
      </div>

      <Card className="border border-slate-200" data-testid="forecast-weather-card">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="font-outfit text-lg flex items-center gap-2 flex-wrap" data-testid="forecast-weather-title">
            <CloudSun className="w-5 h-5 text-[#065F46]" />
            <span>Météo — {weatherCity}</span>
          </CardTitle>
          <div className="flex items-center gap-2 mt-2">
            <Input
              type="text"
              value={cityInput}
              onChange={(e) => setCityInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') loadWeather(cityInput); }}
              placeholder="Changer de ville…"
              className="h-9 text-sm md:w-64"
              data-testid="weather-city-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadWeather(cityInput)}
              data-testid="weather-city-search-btn"
            >
              <Search className="w-4 h-4 mr-1" /> Rechercher
            </Button>
          </div>
          {cityError ? (
            <p className="text-xs text-red-600 mt-1" data-testid="weather-city-error">{cityError}</p>
          ) : null}
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          {weatherLoading ? (
            <div className="py-8 flex items-center justify-center" data-testid="forecast-weather-loading">
              <Loader2 className="w-6 h-6 animate-spin text-[#065F46]" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid md:grid-cols-3 gap-3" data-testid="forecast-current-weather">
                <div className="rounded-md border border-slate-200 p-3">
                  <p className="text-xs text-slate-500">Température</p>
                  <p className="font-semibold text-slate-900">{cw && cw.temperature !== undefined && cw.temperature !== null ? cw.temperature : '-'} °C</p>
                </div>
                <div className="rounded-md border border-slate-200 p-3">
                  <p className="text-xs text-slate-500">Ressenti</p>
                  <p className="font-semibold text-slate-900">{cw && cw.apparent_temperature !== undefined && cw.apparent_temperature !== null ? cw.apparent_temperature : '-'} °C</p>
                </div>
                <div className="rounded-md border border-slate-200 p-3">
                  <p className="text-xs text-slate-500">Conditions</p>
                  <div className="flex items-center gap-2">
                    <CurrentIcon className={`w-5 h-5 ${getWeatherIconClass(cwCode)}`} data-testid="forecast-current-weather-icon" />
                    <p className="font-semibold text-slate-900">{weatherLabel(cwCode)}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="forecast-next-7-days">
                {next7.slice(0, 8).map((day, index) => {
                  const I = getWeatherIcon(day.weather_code);
                  return (
                    <div key={`${day.date}-${index}`} className="rounded-md border border-slate-200 p-2" data-testid={`forecast-day-card-${index}`}>
                      <p className="text-[10px] text-slate-500">{day.date}</p>
                      <div className="flex items-center gap-1 mb-1">
                        <I className={`w-4 h-4 ${getWeatherIconClass(day.weather_code)}`} data-testid={`forecast-day-icon-${index}`} />
                        <p className="text-xs font-medium text-slate-900">{weatherLabel(day.weather_code)}</p>
                      </div>
                      <p className="text-[10px] text-slate-500">{day.temp_min}° / {day.temp_max}°</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border border-slate-200" data-testid="business-forecast-card">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="font-outfit text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#065F46]" />
            Prévisionnel d'activité
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-6">
          {profileLoading ? (
            <div className="py-8 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#065F46]" />
            </div>
          ) : (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="activity-type" className="text-sm text-slate-700">Type d'activité</Label>
                <Select value={activityType} onValueChange={setActivityType}>
                  <SelectTrigger id="activity-type" className="w-full md:w-72" data-testid="activity-type-select">
                    <SelectValue placeholder="Choisissez votre type d'activité" />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_TYPES.map((t) => (
                      <SelectItem key={t.id} value={t.id} data-testid={`activity-option-${t.id}`}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {activityType === 'commerce' ? (
                <div className="space-y-4" data-testid="commerce-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <NumInput id="c-clients" label="Nombre de clients (mensuel)" value={commerce.monthly_clients} onChange={(v) => updateCommerce('monthly_clients', v)} testId="commerce-monthly-clients" />
                    <NumInput id="c-tarif" label="Tarif moyen par client" suffix="€" value={commerce.avg_price} onChange={(v) => updateCommerce('avg_price', v)} testId="commerce-avg-price" />
                    <NumInput id="c-marge" label="Marge" suffix="%" value={commerce.margin_pct} onChange={(v) => updateCommerce('margin_pct', v)} testId="commerce-margin-pct" />
                    <NumInput id="c-charges" label="Charges (mensuel)" suffix="€" value={commerce.monthly_charges} onChange={(v) => updateCommerce('monthly_charges', v)} testId="commerce-monthly-charges" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-700">Haute saison (sélectionnez les mois)</Label>
                    <MonthGrid selected={commerce.high_season_months} onChange={(m) => updateCommerce('high_season_months', m)} color="high" testId="commerce-high-season" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-700">Basse saison (sélectionnez les mois)</Label>
                    <MonthGrid selected={commerce.low_season_months} onChange={(m) => updateCommerce('low_season_months', m)} color="low" testId="commerce-low-season" />
                  </div>
                </div>
              ) : null}

              {activityType === 'service' ? (
                <div className="space-y-4" data-testid="service-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <NumInput id="s-tarif" label="Tarif journalier moyen" suffix="€" value={service.daily_rate} onChange={(v) => updateService('daily_rate', v)} testId="service-daily-rate" />
                    <NumInput id="s-jours" label="Nombre de jours actifs / mois" value={service.active_days_per_month} onChange={(v) => updateService('active_days_per_month', v)} testId="service-active-days" />
                    <NumInput id="s-clients" label="Nombre de clients (mensuel)" value={service.monthly_clients} onChange={(v) => updateService('monthly_clients', v)} testId="service-monthly-clients" />
                    <NumInput id="s-charges" label="Charges (mensuel)" suffix="€" value={service.monthly_charges} onChange={(v) => updateService('monthly_charges', v)} testId="service-monthly-charges" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-700">Haute saison (sélectionnez les mois)</Label>
                    <MonthGrid selected={service.high_season_months} onChange={(m) => updateService('high_season_months', m)} color="high" testId="service-high-season" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-700">Basse saison (sélectionnez les mois)</Label>
                    <MonthGrid selected={service.low_season_months} onChange={(m) => updateService('low_season_months', m)} color="low" testId="service-low-season" />
                  </div>
                </div>
              ) : null}

              {activityType === 'restauration' ? (
                <div className="space-y-4" data-testid="restauration-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <NumInput id="r-couverts" label="Nombre de couverts / jour (capacité)" value={restauration.daily_covers} onChange={(v) => updateRestauration('daily_covers', v)} testId="restauration-daily-covers" />
                    <NumInput id="r-tarif" label="Tarif moyen par client" suffix="€" value={restauration.avg_price} onChange={(v) => updateRestauration('avg_price', v)} testId="restauration-avg-price" />
                    <NumInput id="r-taux" label="Taux de remplissage / jour" suffix="%" value={restauration.fill_rate_pct} onChange={(v) => updateRestauration('fill_rate_pct', v)} testId="restauration-fill-rate" />
                    <NumInput id="r-jours" label="Jours d'ouverture / mois" value={restauration.opening_days_per_month} onChange={(v) => updateRestauration('opening_days_per_month', v)} testId="restauration-opening-days" />
                    <NumInput id="r-charges" label="Charges (mensuel)" suffix="€" value={restauration.monthly_charges} onChange={(v) => updateRestauration('monthly_charges', v)} testId="restauration-monthly-charges" />
                  </div>
                </div>
              ) : null}

              {activityType === 'autre' ? (
                <div className="space-y-4" data-testid="autre-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <NumInput id="a-clients" label="Nombre de clients / mois" value={autre.monthly_clients} onChange={(v) => updateAutre('monthly_clients', v)} testId="autre-monthly-clients" />
                    <NumInput id="a-tarif" label="Tarif moyen / client" suffix="€" value={autre.avg_price} onChange={(v) => updateAutre('avg_price', v)} testId="autre-avg-price" />
                    <NumInput id="a-jours" label="Nombre de jours travaillés / mois" value={autre.worked_days_per_month} onChange={(v) => updateAutre('worked_days_per_month', v)} testId="autre-worked-days" />
                    <NumInput id="a-charges" label="Charges (mensuel)" suffix="€" value={autre.monthly_charges} onChange={(v) => updateAutre('monthly_charges', v)} testId="autre-monthly-charges" />
                  </div>
                </div>
              ) : null}

              {activityType ? (
                <div className="flex justify-end gap-2">
                  <Button
                    onClick={handleExportPDF}
                    disabled={!forecast}
                    variant="outline"
                    data-testid="export-forecast-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-2" /> Export PDF détaillé
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={savingProfile}
                    className="bg-[#065F46] hover:bg-[#047857] text-white"
                    data-testid="save-business-profile-btn"
                  >
                    {savingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Enregistrer
                  </Button>
                </div>
              ) : null}

              <div className="border-t border-slate-200 pt-4">
                <p className="text-xs uppercase tracking-wide font-medium text-slate-500 mb-3 flex items-center gap-2">
                  <PieChart className="w-3.5 h-3.5" /> Résultat prévisionnel annuel
                </p>
                {!forecast ? (
                  <p className="text-sm text-slate-400 italic" data-testid="forecast-empty">
                    Renseignez les données ci-dessus pour voir votre prévisionnel.
                  </p>
                ) : (
                  <div className="space-y-4" data-testid="business-forecast-result">
                    <div className="grid md:grid-cols-3 gap-3">
                      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4" data-testid="forecast-annual-ca">
                        <p className="text-xs text-emerald-700 uppercase tracking-wide font-medium">CA annuel prévisionnel</p>
                        <p className="font-outfit text-2xl font-bold text-[#065F46] mt-1 font-mono">{fmtEur(forecast.annualCA)}</p>
                      </div>
                      <div className="rounded-md border border-rose-200 bg-rose-50 p-4" data-testid="forecast-annual-charges">
                        <p className="text-xs text-rose-700 uppercase tracking-wide font-medium">Charges annuelles</p>
                        <p className="font-outfit text-2xl font-bold text-rose-700 mt-1 font-mono">{fmtEur(forecast.annualCharges)}</p>
                      </div>
                      <div className={profitBoxCls} data-testid="forecast-annual-profit">
                        <p className="text-xs uppercase tracking-wide font-medium opacity-70">Bénéfice annuel</p>
                        <p className={`font-outfit text-2xl font-bold mt-1 font-mono ${profitColor}`}>{fmtEur(forecast.netProfit)}</p>
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-4" data-testid="forecast-breakdown">
                      <p className="text-xs uppercase tracking-wide font-medium text-slate-500 mb-2 flex items-center gap-2">
                        <Calculator className="w-3.5 h-3.5" /> Détail du calcul
                      </p>
                      <ol className="space-y-1.5 text-sm text-slate-700">
                        {breakdownLines.map((line, i) => (
                          <li key={i} className="flex gap-2" data-testid={`forecast-breakdown-step-${i}`}>
                            <span className="text-slate-400 font-mono shrink-0">{i + 1}.</span>
                            <span>{line}</span>
                          </li>
                        ))}
                      </ol>
                    </div>

                    {previousYearData ? (
                      <div className="rounded-md border border-sky-200 bg-sky-50 p-4" data-testid="previous-year-comparison">
                        <p className="text-xs uppercase tracking-wide font-medium text-sky-700 mb-2 flex items-center gap-2">
                          <PieChart className="w-3.5 h-3.5" /> Comparaison avec l'année précédente
                        </p>
                        <div className="grid md:grid-cols-3 gap-3 text-sm">
                          <div>
                            <p className="text-xs text-slate-500">CA réel {previousYearData.previous_year}</p>
                            <p className="font-mono font-bold text-slate-900">{fmtEur(previousYearData.previous_year_ca || 0)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">CA réel {previousYearData.current_year} (au {new Date().toLocaleDateString('fr-FR')})</p>
                            <p className="font-mono font-bold text-slate-900">{fmtEur(previousYearData.current_year_ca_to_date || 0)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Évolution N-1 → N</p>
                            {previousYearData.delta_pct === null ? (
                              <p className="font-mono font-bold text-slate-400 text-xs">Aucune donnée N-1</p>
                            ) : (
                              <p className={`font-mono font-bold ${previousYearData.delta_pct >= 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="previous-year-delta">
                                {previousYearData.delta_pct >= 0 ? '▲' : '▼'} {previousYearData.delta_pct > 0 ? '+' : ''}{previousYearData.delta_pct}%
                              </p>
                            )}
                          </div>
                        </div>
                        {forecast && previousYearData.previous_year_ca > 0 ? (
                          <p className="text-xs text-slate-600 mt-3 pt-3 border-t border-sky-200">
                            <b>Prévisionnel vs année précédente :</b> CA prévu {fmtEur(forecast.annualCA)} vs {fmtEur(previousYearData.previous_year_ca)} réalisé en {previousYearData.previous_year} (
                            <span className={forecast.annualCA >= previousYearData.previous_year_ca ? 'text-emerald-700 font-semibold' : 'text-red-600 font-semibold'}>
                              {forecast.annualCA >= previousYearData.previous_year_ca ? '▲' : '▼'} {((forecast.annualCA - previousYearData.previous_year_ca) / previousYearData.previous_year_ca * 100).toFixed(1)}%
                            </span>)
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PrevisionnelPage;
